export const SKILL_PAGE_SIZE = 25;

export const SKILL_FILTERS = ['all', 'published', 'retired', 'internal'] as const;
export type SkillFilter = (typeof SKILL_FILTERS)[number];

export type SkillsListQuery = { q: string; filter: SkillFilter; offset: number };

/** The list's search, filter and page from the address (`?q=&filter=&offset=`). */
export function skillsQueryFrom(params: URLSearchParams): SkillsListQuery {
  const filter = params.get('filter') ?? '';
  const offset = Number.parseInt(params.get('offset') ?? '', 10);
  return {
    q: params.get('q')?.trim() ?? '',
    filter: (SKILL_FILTERS as readonly string[]).includes(filter) ? (filter as SkillFilter) : 'all',
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  };
}

/** The address for a list view, leaving out defaults so a plain /skills stays plain. */
export function skillsQueryParams(query: SkillsListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.filter !== 'all') params.set('filter', query.filter);
  if (query.offset > 0) params.set('offset', String(query.offset));
  return params;
}

/** The next view: a new search or filter goes back to the first page. */
export function nextSkillsQuery(current: SkillsListQuery, change: Partial<SkillsListQuery>): SkillsListQuery {
  const next = { ...current, ...change };
  if (next.q !== current.q || next.filter !== current.filter) next.offset = change.offset ?? 0;
  return next;
}

const EMPTY_LABELS: Record<SkillFilter, string> = {
  all: 'No global skills yet. Create one with New global skill.',
  published: 'No skills are published. Publish one to offer it to organizations.',
  retired: 'No skills are retired.',
  internal: 'No skills are for RapidSOS only.',
};

/** What the list says when the page is empty. */
export function skillsEmptyLabel(query: Pick<SkillsListQuery, 'q' | 'filter'>): string {
  if (query.q) return `No skills match “${query.q}”. Try a different word or clear the search.`;
  return EMPTY_LABELS[query.filter];
}
